import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, take } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import {
  ApprovedContent,
  ApprovedContentDownload,
  ApprovedContentDownloadRequest,
} from './approved-content.types';

export type ApprovedContentState =
  | { status: 'idle' }
  | { status: 'loading'; briefId: string }
  | { status: 'error'; briefId: string }
  | { status: 'loaded'; briefId: string; data: ApprovedContent };

/**
 * Approved Content for the focussed brief. Root-provided so the Manage Brief shell (tab
 * badge) and the tab itself share one request per brief.
 */
@Injectable({ providedIn: 'root' })
export class ApprovedContentService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.shout_api.url}/approved-content`;

  readonly state = signal<ApprovedContentState>({ status: 'idle' });

  /** Loads the brief's approved videos; reloads when asked or when the brief changes. */
  load(briefId: string, force = false): void {
    const current = this.state();
    if (!force && current.status !== 'idle' && current.status !== 'error' && current.briefId === briefId) return;
    this.state.set({ status: 'loading', briefId });
    this.http
      .get<ApprovedContent>(`${this.baseUrl}/${briefId}`)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          if (this.briefIdOf(this.state()) === briefId) this.state.set({ status: 'loaded', briefId, data });
        },
        error: (err) => {
          console.error('approved-content load failed', err);
          if (this.briefIdOf(this.state()) === briefId) this.state.set({ status: 'error', briefId });
        },
      });
  }

  download(briefId: string, request: ApprovedContentDownloadRequest): Observable<ApprovedContentDownload> {
    return this.http.post<ApprovedContentDownload>(`${this.baseUrl}/${briefId}/download`, request).pipe(take(1));
  }

  /** Marks videos as downloaded by this user without refetching. */
  markDownloaded(briefId: string, videoIds: readonly string[]): void {
    const current = this.state();
    if (current.status !== 'loaded' || current.briefId !== briefId) return;
    const ids = new Set(videoIds);
    this.state.set({
      ...current,
      data: {
        ...current.data,
        videos: current.data.videos.map((v) => (ids.has(v.id) ? { ...v, downloadedByMe: true } : v)),
      },
    });
  }

  private briefIdOf(state: ApprovedContentState): string | null {
    return state.status === 'idle' ? null : state.briefId;
  }
}
